import React from 'react';
import PropTypes from 'prop-types';
import {TextCell} from './TextCell';
import {InputCell} from './InputCell';
import {DropdownCell} from './DropdownCell';
import {CELL_TYPES, DATA_TYPE} from './DataTable.constants';
import './style.scss';
import parse from 'html-react-parser';

export function DataTable(props) {
  const onCellChange = (params) => props.onCellChange(params);

  const setIsCorrect = (cell) => {
    if (typeof cell.answer === DATA_TYPE.STRING) {
      return cell.answer.toLowerCase() === cell.value.toLowerCase();
    }
    if (typeof cell.answer === DATA_TYPE.NUMBER) {
      return cell.answer === cell.value;
    }
  };

  const renderDropdown = (cell, cellId) =>
    React.createElement(DropdownCell, {
      key: cellId,
      placeholder: cell.placeholder,
      className: 'dataTable_row_cell',
      options: cell.options,
      value: cell.value,
      id: cellId,
      onChange: onCellChange,
      disabled: cell.disabled,
      showFeedback: cell.showFeedback,
      isCorrect: setIsCorrect(cell),
      feedback: (typeof cell.feedback === 'string')?parse(cell.feedback):cell.feedback
    });

  const renderInputCell = (cell, cellId) =>
    React.createElement(InputCell, {
      key: cellId,
      id: cellId,
      placeholder: cell.placeholder,
      className: 'dataTable_row_cell',
      type: cell.inputType,
      value: cell.value,
      onChange: onCellChange,
      disabled: cell.disabled,
      showFeedback: cell.showFeedback,
      isCorrect: setIsCorrect(cell),
      feedback: (typeof cell.feedback === 'string')?parse(cell.feedback):cell.feedback
    });

  const renderTitleCell = (cell, cellId) =>
    React.createElement(TextCell, {
      key: cellId,
      id: cellId,
      className: `dataTable_row_${cell.type.toLowerCase()}`,
      text: cell.text,
    });

  const renderCellByType = (cell, cellId) => {
    switch (cell.type) {
      case CELL_TYPES.DROPDOWN:
        return renderDropdown(cell, cellId);
      case CELL_TYPES.INPUT:
        return renderInputCell(cell, cellId);
      case CELL_TYPES.TITLE:
      case CELL_TYPES.FIXED:
        return renderTitleCell(cell, cellId);
      default:
        return null;
    }
  };

  const renderCell = (rowIndex) => (cell, colIndex) =>
    renderCellByType(cell, `${rowIndex}_${colIndex}`);

  const [rows, columns] = props.indices;

  const rowStyle = {
    gridTemplateColumns: `repeat(${columns},1fr)`,
  };

  const renderRow = (row, key) => (
    <div key={key} style={rowStyle} className='dataTable_row'>
      {row.map(renderCell(key))}
    </div>
  );

  const tableStyle = {
    gridTemplateRows: `repeat(${rows},1fr)`,
  };

  return (
    <div className='dataTable' style={tableStyle}>
      {props.data.map(renderRow)}
    </div>
  );
}

DataTable.propTypes = {
  /**
   * Number of [rows, columns] in the table
   */
  indices: PropTypes.arrayOf(PropTypes.number),
  /**
   * Data to be rendered in the table
   */
  data: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * The type of the cell
         */
        type: PropTypes.oneOf(['TITLE', 'FIXED', 'INPUT', 'DROPDOWN']),
        /**
         * Text to render in cell [ TITLE, FIXED]
         */
        text: PropTypes.text,
        /**
         * Type of the input [ INPUT ]
         */
        inputType: PropTypes.oneOf(['num', 'text']),
        /**
         * placeholder of for cell [ DROPDOWN, INPUT]
         */
        placeholder: PropTypes.string,
        /**
         * options to display on dropdown [DROPDOWN]
         */
        options: PropTypes.array,
        /**
         * Boolean to disable the cell
         */
        disabled: PropTypes.bool,
        /**
         * Boolean to set if feedback should be displayed
         */
        showFeedback: PropTypes.bool,
      })
    )
  ),
  /**
   * Function to invoke when the data in a cell changes
   */
  onCellChange: PropTypes.func,
};
